import { useEffect, useState } from 'react'

import { navigateTo } from '../../../common/frontend/route-utils.js'
import { t } from '../../../common/i18n.js'
import type { ProfileSection } from '../../components/me-layout/me-section.js'
import { emptyPortalPermissions, hasAnyAdmin, hasPerm } from '../../portal-role.js'
import { showToast } from '../../toast.js'
import { View } from '../../types.js'
import { setLanguage } from '../../utils/language.js'
import { loadPortalRoleIfNeeded } from '../../utils/load-utils.js'
import { setTheme } from '../../utils/theme.js'
import { getDisplayName } from './utils/format.js'
import {
    addEmergencyContact,
    addEmployeeDocument,
    addExperience,
    cancelTimeOffRequest,
    createCompensation,
    createCompensationLineItem,
    createEmploymentRecord,
    decideFieldChangeRequest,
    decideTimeOffCancellation,
    decideTimeOffRequest,
    deleteCompensation,
    deleteCompensationLineItem,
    deleteEmergencyContact,
    deleteEmployeeDocument,
    deleteEmploymentRecord,
    deleteExperience,
    editTimeOffRequest,
    fetchCompanyHolidays,
    fetchCompensation,
    fetchCompensationLineItems,
    fetchCompensationTypes,
    fetchEmergencyContacts,
    fetchEmployee,
    fetchEmployeeCustomFields,
    fetchEmployeeDocuments,
    fetchEmployeeOptions,
    fetchEmployeeTimeOff,
    fetchEmploymentHistory,
    fetchExperiences,
    fetchFieldChangeRequests,
    fetchLeaveSettlement,
    fetchLocations,
    fetchTeams,
    fetchWorkingPatterns,
    offboardEmployee,
    requestCustomLocationChange,
    requestLocationChange,
    revealCompensation,
    saveEmployeeAvatar,
    saveEmployeeCustomFields,
    saveMyAvatar,
    setEmployeeStatus,
    updateCompensation,
    updateCompensationLineItem,
    updateEmergencyContact,
    updateEmployee,
    updateEmployeeDocument,
    updateEmploymentDates,
    updateExperience,
    uploadAvatarImage,
} from './utils/portal-api.js'
import type { PendingFieldChange } from './utils/portal-api.js'
import type {
    CompensationData,
    CompensationLineItemGroup,
    CompensationLineItemInput,
    CompensationType,
    CustomFieldWithValue,
    EmergencyContact,
    EmployeeDocument,
    EmployeeProfileData,
    EmploymentHistoryRecord,
    EmploymentRefData,
    ExperienceEntry,
    LeaveRequest,
    LeaveSettlement,
    ProfileLeaveBalance,
    WorkingPatternHistoryRecord,
} from './views/employee-profile/employee-profile-view.js'
import { EmployeeProfileView } from './views/employee-profile/employee-profile-view.js'

export const EmployeeProfileRoute = ({
    view,
    employeeId,
    showSignature,
    meContext,
    section,
}: {
    view: View
    employeeId: number
    showSignature?: boolean
    // True on the self /me profile (chromeless, MeLayout supplies the chrome).
    meContext?: boolean
    section?: ProfileSection
}) => {
    const lang = view.state.lang

    const portalRole = loadPortalRoleIfNeeded(view).data
    // Admin-ish UI affordances follow any held admin capability; the server
    // enforces the actual per-field/per-surface permissions.
    const isPortalAdmin = hasAnyAdmin(portalRole?.permissions ?? emptyPortalPermissions())
    // The TLS settlement preview is payroll-sensitive: only the specific
    // time-away admin capability may see it (matches the server gate) — not
    // every holder of some unrelated admin key.
    const canSeeSettlement = hasPerm(
        portalRole?.permissions ?? emptyPortalPermissions(),
        'admin',
        'manage_time_away_settings',
    )
    // Offboarding (status flip) is its own capability, separate from the field
    // edits the admin UI exposes; matches the server gate on the status endpoint.
    const canOffboardUsers = hasPerm(
        portalRole?.permissions ?? emptyPortalPermissions(),
        'profile',
        'offboard_users',
    )
    const myEmployeeId = portalRole?.myEmployeeId ?? null

    // Employee profile state
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeProfileData | null>(null)
    const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([])
    // "Kogemused" repeater entries (TASK-300) — the GET is view-gated server-side.
    const [experiences, setExperiences] = useState<ExperienceEntry[]>([])

    const [isLoadingProfile, setIsLoadingProfile] = useState(false)
    // Profile time-off state
    const [profileLeaveBalances, setProfileLeaveBalances] = useState<ProfileLeaveBalance[]>([])
    const [profileLeaveRequests, setProfileLeaveRequests] = useState<LeaveRequest[]>([])
    const [companyHolidays, setCompanyHolidays] = useState<{ date: string; name: string }[]>([])
    const [leaveSettlement, setLeaveSettlement] = useState<LeaveSettlement | null>(null)

    // Profile error state
    const [profileError, setProfileError] = useState<string | null>(null)

    // M4: Compensation & Contract state
    const [compensationData, setCompensationData] = useState<CompensationData | null>(null)
    const [compensationLineItems, setCompensationLineItems] = useState<
        CompensationLineItemGroup[] | null
    >(null)
    const [compensationTypes, setCompensationTypes] = useState<CompensationType[]>([])
    const [employmentHistory, setEmploymentHistory] = useState<EmploymentHistoryRecord[]>([])
    const [workingPatterns, setWorkingPatterns] = useState<WorkingPatternHistoryRecord[]>([])

    // Custom fields state
    const [customFields, setCustomFields] = useState<CustomFieldWithValue[]>([])

    // Document folders state
    const [documents, setDocuments] = useState<EmployeeDocument[]>([])

    // Reference lists for the admin "edit employment" pickers (admins only).
    const [employmentRefData, setEmploymentRefData] = useState<EmploymentRefData | undefined>(
        undefined,
    )

    // Manager-confirmed work-location change flow (pending request + viewer role)
    const [fieldChange, setFieldChange] = useState<{
        pending: PendingFieldChange | null
        canDecide: boolean
    } | null>(null)
    const [locationOptions, setLocationOptions] = useState<{ id: number; name: string }[]>([])

    // Load employee profile
    useEffect(() => {
        if (!employeeId) {
            setSelectedEmployee(null)
            return
        }

        setIsLoadingProfile(true)
        setProfileError(null)
        // Drop the previous profile's payload (and its viewer caps) so its
        // "Muuda" affordances never flash on the next profile while loading.
        setSelectedEmployee(null)
        setEmploymentRefData(undefined)
        void (async () => {
            try {
                const [empRes, contactsRes, timeOffRes, histRes, wpRes, expRes] = await Promise.all(
                    [
                        fetchEmployee(employeeId),
                        fetchEmergencyContacts(employeeId),
                        fetchEmployeeTimeOff(employeeId),
                        fetchEmploymentHistory(employeeId),
                        fetchWorkingPatterns(employeeId),
                        fetchExperiences(employeeId),
                    ],
                )
                if (empRes.ok) setSelectedEmployee(empRes.data)
                if (contactsRes.ok) setEmergencyContacts(contactsRes.data)
                // View-gated server-side: a 403 → not ok → empty list (as contacts).
                setExperiences(expRes.ok ? expRes.data : [])
                if (timeOffRes.ok) {
                    setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                    setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                }
                // Employment history + working patterns share the job_title
                // view permission, so the self-baseline covers self.
                setEmploymentHistory(histRes.ok ? histRes.data : [])
                setWorkingPatterns(wpRes.ok ? wpRes.data : [])

                // Public holidays of the subject's primary company, for the
                // time-away Details card ("next holiday").
                const holidayCompanyId = empRes.ok
                    ? (empRes.data.companies.find((c) => c.is_primary)?.id ?? 1)
                    : 1
                const holidaysRes = await fetchCompanyHolidays(
                    new Date().getFullYear(),
                    holidayCompanyId,
                )
                setCompanyHolidays(holidaysRes.ok ? holidaysRes.data : [])

                // Custom fields
                const cfRes = await fetchEmployeeCustomFields(employeeId)
                setCustomFields(cfRes.ok ? cfRes.data : [])

                // Document folders
                const docsRes = await fetchEmployeeDocuments(employeeId)
                setDocuments(docsRes.ok ? docsRes.data : [])

                // Reference lists for the employment editor — only for viewers
                // who may edit this person's employment (permission-driven).
                if (empRes.ok && empRes.data.viewer?.canEditEmployment) {
                    const [teamsRes, locationsRes, employeesRes] = await Promise.all([
                        fetchTeams(),
                        fetchLocations(),
                        fetchEmployeeOptions(),
                    ])
                    setEmploymentRefData({
                        teams: teamsRes.ok ? teamsRes.data : [],
                        locations: locationsRes.ok ? locationsRes.data : [],
                        employees: employeesRes.ok ? employeesRes.data : [],
                    })
                }

                // Work-location change flow: pending request + viewer's role in
                // it (the endpoint 403s for uninvolved viewers — that's fine).
                const fcRes = await fetchFieldChangeRequests(employeeId)
                setFieldChange(fcRes.ok ? fcRes.data : null)
                if (myEmployeeId === employeeId && !isPortalAdmin) {
                    const locRes = await fetchLocations()
                    setLocationOptions(locRes.ok ? locRes.data : [])
                }

                // M4: Compensation, contracts & line items — work data, gated by
                // the server-resolved capability (self ∨ salary-view ∨ direct
                // manager); the server enforces it on each endpoint too.
                const canSeeWorkData = empRes.ok && (empRes.data.viewer?.canViewWorkData ?? false)
                if (canSeeWorkData) {
                    const [compRes, lineItemsRes, typesRes] = await Promise.all([
                        fetchCompensation(employeeId),
                        fetchCompensationLineItems(employeeId),
                        fetchCompensationTypes(),
                    ])
                    setCompensationData(compRes.ok ? compRes.data : null)
                    setCompensationLineItems(lineItemsRes.ok ? lineItemsRes.data : null)
                    setCompensationTypes(typesRes.ok ? typesRes.data : [])
                }

                // TLS §69/§71 settlement preview — payroll-sensitive, admin
                // capability only (server gates on manage_time_away_settings).
                if (canSeeSettlement) {
                    const settlementRes = await fetchLeaveSettlement(employeeId)
                    setLeaveSettlement(settlementRes.ok ? settlementRes.data : null)
                } else {
                    setLeaveSettlement(null)
                }
            } catch {
                setProfileError(t.portal.profile.notFound(lang))
            } finally {
                setIsLoadingProfile(false)
            }
        })()
    }, [employeeId, isPortalAdmin, canSeeSettlement, myEmployeeId])

    if (profileError) {
        return (
            <div className="portal-profile">
                <div className="portal-profile__loading">{profileError}</div>
            </div>
        )
    }

    const isMeContext = meContext === true

    const isSelf = myEmployeeId === employeeId
    // Server-resolved per-target capabilities — the single source the edit
    // callbacks below are gated on (the server re-enforces every write).
    const viewer = selectedEmployee?.viewer

    // Manager-confirmed work-location change flow. Admins skip the request
    // path (they edit employment directly); the employee requests, the direct
    // manager/admin decides.
    const refreshFieldChange = async () => {
        const fcRes = await fetchFieldChangeRequests(employeeId)
        setFieldChange(fcRes.ok ? fcRes.data : null)
        const empRes = await fetchEmployee(employeeId)
        if (empRes.ok) setSelectedEmployee(empRes.data)
    }
    const canRequestLocation = myEmployeeId === employeeId && !isPortalAdmin
    const locationChange =
        canRequestLocation || fieldChange?.pending
            ? {
                  pending: fieldChange?.pending
                      ? {
                            id: fieldChange.pending.id,
                            newLabel: fieldChange.pending.newLabel,
                            oldLabel: fieldChange.pending.oldLabel,
                        }
                      : null,
                  canDecide: fieldChange?.canDecide ?? false,
                  canRequest: canRequestLocation,
                  locations: locationOptions,
                  onRequest: async (locationId: number) => {
                      const res = await requestLocationChange(employeeId, locationId)
                      if (!res.ok) return false
                      await refreshFieldChange()
                      return true
                  },
                  onRequestCustom: async (name: string, countryCode: string, timezone?: string) => {
                      const res = await requestCustomLocationChange(
                          employeeId,
                          name,
                          countryCode,
                          timezone,
                      )
                      if (!res.ok) return false
                      await refreshFieldChange()
                      return true
                  },
                  onDecide: async (requestId: number, action: 'approve' | 'reject') => {
                      const res = await decideFieldChangeRequest(employeeId, requestId, action)
                      if (!res.ok) return false
                      await refreshFieldChange()
                      return true
                  },
              }
            : undefined

    const profileView = (
        <EmployeeProfileView
            employee={selectedEmployee}
            emergencyContacts={emergencyContacts}
            leaveBalances={profileLeaveBalances}
            leaveRequests={profileLeaveRequests}
            leaveSettlement={leaveSettlement}
            companyHolidays={companyHolidays}
            compensationData={compensationData}
            compensationLineItems={compensationLineItems}
            employmentHistory={employmentHistory}
            workingPatterns={workingPatterns}
            customFields={customFields}
            documents={documents}
            isLoading={isLoadingProfile}
            isSelf={myEmployeeId === employeeId}
            isAdmin={isPortalAdmin}
            view={view}
            lang={lang}
            onSelectLanguage={(l) => setLanguage(view, l)}
            themePreference={view.state.themePreference}
            onSelectTheme={(pref, origin) => setTheme(view, pref, origin)}
            showSignature={showSignature}
            locationChange={locationChange}
            onUpdatePersonal={
                (viewer?.canEditPersonal ||
                    viewer?.canEditHealth ||
                    viewer?.canEditName ||
                    viewer?.canEditWorkEmail ||
                    viewer?.canEditRoleDetails ||
                    viewer?.canEditContact) &&
                employeeId
                    ? async (data) => {
                          const res = await updateEmployee(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const empRes = await fetchEmployee(employeeId)
                          if (empRes.ok) setSelectedEmployee(empRes.data)
                          return true
                      }
                    : undefined
            }
            canOffboardUsers={canOffboardUsers}
            onChangeStatus={
                canOffboardUsers && employeeId
                    ? async (status) => {
                          const res = await setEmployeeStatus(employeeId, status)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const empRes = await fetchEmployee(employeeId)
                          if (empRes.ok) setSelectedEmployee(empRes.data)
                          return true
                      }
                    : undefined
            }
            onOffboard={
                canOffboardUsers && employeeId
                    ? async (data) => {
                          const res = await offboardEmployee(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const empRes = await fetchEmployee(employeeId)
                          if (empRes.ok) setSelectedEmployee(empRes.data)
                          return true
                      }
                    : undefined
            }
            onSaveCustomFields={
                customFields.some((f) => f.can_edit) && employeeId
                    ? async (values) => {
                          const res = await saveEmployeeCustomFields(employeeId, values)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const cfRes = await fetchEmployeeCustomFields(employeeId)
                          if (cfRes.ok) setCustomFields(cfRes.data)
                          return true
                      }
                    : undefined
            }
            onCreateCompensation={
                viewer?.canEditWorkData && employeeId
                    ? async (data) => {
                          const res = await createCompensation(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const compRes = await fetchCompensation(employeeId)
                          if (compRes.ok) setCompensationData(compRes.data)
                          return true
                      }
                    : undefined
            }
            onUpdateCompensation={
                viewer?.canEditWorkData && employeeId
                    ? async (compId, data) => {
                          const res = await updateCompensation(employeeId, compId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const compRes = await fetchCompensation(employeeId)
                          if (compRes.ok) setCompensationData(compRes.data)
                          return true
                      }
                    : undefined
            }
            onRevealCompensation={
                viewer?.canViewCompensation && employeeId
                    ? async () => {
                          await revealCompensation(employeeId)
                      }
                    : undefined
            }
            onUpdateEmploymentDates={
                viewer?.canEditEmployment && employeeId
                    ? async (data) => {
                          const res = await updateEmploymentDates(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const empRes2 = await fetchEmployee(employeeId)
                          if (empRes2.ok) setSelectedEmployee(empRes2.data)
                          return true
                      }
                    : undefined
            }
            employmentRefData={employmentRefData}
            onCreateEmploymentRecord={
                viewer?.canEditEmployment && employeeId
                    ? async (data) => {
                          const res = await createEmploymentRecord(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          // Refetch both the profile (card reflects new active
                          // record) and the history timeline (new dated row).
                          const [empRes2, histRes2] = await Promise.all([
                              fetchEmployee(employeeId),
                              fetchEmploymentHistory(employeeId),
                          ])
                          if (empRes2.ok) setSelectedEmployee(empRes2.data)
                          if (histRes2.ok) setEmploymentHistory(histRes2.data)
                          return true
                      }
                    : undefined
            }
            onAddEmergencyContact={
                viewer?.canEditEmergency && employeeId
                    ? async (data) => {
                          const res = await addEmergencyContact(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const cRes = await fetchEmergencyContacts(employeeId)
                          if (cRes.ok) setEmergencyContacts(cRes.data)
                          return true
                      }
                    : undefined
            }
            onUpdateEmergencyContact={
                viewer?.canEditEmergency && employeeId
                    ? async (contactId, data) => {
                          const res = await updateEmergencyContact(employeeId, contactId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const cRes = await fetchEmergencyContacts(employeeId)
                          if (cRes.ok) setEmergencyContacts(cRes.data)
                          return true
                      }
                    : undefined
            }
            onDeleteEmergencyContact={
                viewer?.canEditEmergency && employeeId
                    ? async (contactId) => {
                          const res = await deleteEmergencyContact(employeeId, contactId)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          setEmergencyContacts((prev) => prev.filter((c) => c.id !== contactId))
                          return true
                      }
                    : undefined
            }
            experiences={experiences}
            onAddExperience={
                viewer?.canEditExperiences && employeeId
                    ? async (data) => {
                          const res = await addExperience(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const eRes = await fetchExperiences(employeeId)
                          if (eRes.ok) setExperiences(eRes.data)
                          return true
                      }
                    : undefined
            }
            onUpdateExperience={
                viewer?.canEditExperiences && employeeId
                    ? async (entryId, data) => {
                          const res = await updateExperience(employeeId, entryId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const eRes = await fetchExperiences(employeeId)
                          if (eRes.ok) setExperiences(eRes.data)
                          return true
                      }
                    : undefined
            }
            onDeleteExperience={
                viewer?.canEditExperiences && employeeId
                    ? async (entryId) => {
                          const res = await deleteExperience(employeeId, entryId)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          setExperiences((prev) => prev.filter((e) => e.id !== entryId))
                          return true
                      }
                    : undefined
            }
            onAddDocument={
                viewer?.canManageDocuments && employeeId
                    ? async (data) => {
                          const res = await addEmployeeDocument(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const docsRes = await fetchEmployeeDocuments(employeeId)
                          if (docsRes.ok) setDocuments(docsRes.data)
                          return true
                      }
                    : undefined
            }
            onUpdateDocument={
                viewer?.canManageDocuments && employeeId
                    ? async (documentId, data) => {
                          const res = await updateEmployeeDocument(employeeId, documentId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const docsRes = await fetchEmployeeDocuments(employeeId)
                          if (docsRes.ok) setDocuments(docsRes.data)
                          return true
                      }
                    : undefined
            }
            onDeleteDocument={
                viewer?.canManageDocuments && employeeId
                    ? async (documentId) => {
                          const res = await deleteEmployeeDocument(employeeId, documentId)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          setDocuments((prev) => prev.filter((d) => d.id !== documentId))
                          return true
                      }
                    : undefined
            }
            onDeleteCompensation={
                viewer?.canEditWorkData && employeeId
                    ? async (compId) => {
                          const res = await deleteCompensation(employeeId, compId)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const compRes = await fetchCompensation(employeeId)
                          if (compRes.ok) setCompensationData(compRes.data)
                          return true
                      }
                    : undefined
            }
            compensationTypes={compensationTypes}
            onCreateLineItem={
                viewer?.canEditWorkData && employeeId
                    ? async (data: CompensationLineItemInput) => {
                          const res = await createCompensationLineItem(employeeId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const liRes = await fetchCompensationLineItems(employeeId)
                          if (liRes.ok) setCompensationLineItems(liRes.data)
                          return true
                      }
                    : undefined
            }
            onUpdateLineItem={
                viewer?.canEditWorkData && employeeId
                    ? async (itemId: number, data: CompensationLineItemInput) => {
                          const res = await updateCompensationLineItem(employeeId, itemId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const liRes = await fetchCompensationLineItems(employeeId)
                          if (liRes.ok) setCompensationLineItems(liRes.data)
                          return true
                      }
                    : undefined
            }
            onDeleteLineItem={
                viewer?.canEditWorkData && employeeId
                    ? async (itemId: number) => {
                          const res = await deleteCompensationLineItem(employeeId, itemId)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const liRes = await fetchCompensationLineItems(employeeId)
                          if (liRes.ok) setCompensationLineItems(liRes.data)
                          return true
                      }
                    : undefined
            }
            onDeleteEmploymentRecord={
                viewer?.canEditEmployment && employeeId
                    ? async (recordId) => {
                          const res = await deleteEmploymentRecord(employeeId, recordId)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const histRes = await fetchEmploymentHistory(employeeId)
                          if (histRes.ok) setEmploymentHistory(histRes.data)
                          return true
                      }
                    : undefined
            }
            onWorkingPatternsRefresh={
                viewer?.canEditEmployment && employeeId
                    ? async () => {
                          const wpRes = await fetchWorkingPatterns(employeeId)
                          if (wpRes.ok) setWorkingPatterns(wpRes.data)
                      }
                    : undefined
            }
            onAvatarChange={
                isSelf || isPortalAdmin
                    ? async (file) => {
                          const uploadRes = await uploadAvatarImage(file)
                          if (!uploadRes.ok) return false
                          const saveRes = isSelf
                              ? await saveMyAvatar(uploadRes.data.imageUrl)
                              : await saveEmployeeAvatar(employeeId, uploadRes.data.imageUrl)
                          if (!saveRes.ok) return false
                          const empRes = await fetchEmployee(employeeId)
                          if (empRes.ok) setSelectedEmployee(empRes.data)
                          if (isSelf) {
                              view.state.portalRole.data = undefined
                              view.update()
                          }
                          return true
                      }
                    : undefined
            }
            onAvatarRemove={
                isSelf || isPortalAdmin
                    ? async () => {
                          const res = isSelf
                              ? await saveMyAvatar(null)
                              : await saveEmployeeAvatar(employeeId, null)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const empRes = await fetchEmployee(employeeId)
                          if (empRes.ok) setSelectedEmployee(empRes.data)
                          if (isSelf) {
                              view.state.portalRole.data = undefined
                              view.update()
                          }
                          return true
                      }
                    : undefined
            }
            onNavigate={navigateTo}
            chromeless={isMeContext}
            activeSection={section}
            onBookTimeAway={
                // Self books their own; managing others' bookings follows the
                // time-away manage capability (the server re-checks too).
                isSelf || viewer?.canManageTimeAway
                    ? () =>
                          view.state._openLeaveBooking?.({
                              targetEmployee:
                                  myEmployeeId !== employeeId && selectedEmployee
                                      ? {
                                            id: employeeId,
                                            name: getDisplayName(selectedEmployee),
                                        }
                                      : undefined,
                              onSuccess: () => {
                                  void (async () => {
                                      const timeOffRes = await fetchEmployeeTimeOff(employeeId)
                                      if (timeOffRes.ok) {
                                          setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                                          setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                                      }
                                  })()
                              },
                          })
                    : undefined
            }
            onCancelTimeAway={async (requestId) => {
                const res = await cancelTimeOffRequest(requestId)
                if (!res.ok) {
                    showToast(res.error, 'error')
                    return false
                }
                // An owner's retroactive delete of an approval-flow absence is
                // only a request — tell them it went to the manager.
                showToast(
                    res.data.cancel_requested
                        ? t.portal.timeOff.cancelRequestSent(lang)
                        : t.portal.timeOff.requestCancelled(lang),
                    'success',
                )
                const timeOffRes = await fetchEmployeeTimeOff(employeeId)
                if (timeOffRes.ok) {
                    setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                    setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                }
                return true
            }}
            onDecideCancellation={
                viewer?.canManageTimeAway && !isSelf && employeeId
                    ? async (requestId, action) => {
                          const res = await decideTimeOffCancellation(requestId, action)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          // An approved deletion refunds the ledger — refresh balances too.
                          const timeOffRes = await fetchEmployeeTimeOff(employeeId)
                          if (timeOffRes.ok) {
                              setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                              setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                          }
                          return true
                      }
                    : undefined
            }
            onEditTimeAway={
                // Always provided, like cancel — per-row visibility and the
                // server both gate via the shared time-off-edit-rules.
                employeeId
                    ? async (requestId, data) => {
                          const res = await editTimeOffRequest(requestId, data)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          const timeOffRes = await fetchEmployeeTimeOff(employeeId)
                          if (timeOffRes.ok) {
                              setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                              setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                          }
                          return true
                      }
                    : undefined
            }
            onLeaveDataChanged={() => {
                // A bundle change may add/remove policies — refresh balances.
                void (async () => {
                    const timeOffRes = await fetchEmployeeTimeOff(employeeId)
                    if (timeOffRes.ok) {
                        setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                        setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                    }
                })()
            }}
            onDecideTimeAway={
                viewer?.canManageTimeAway && !isSelf && employeeId
                    ? async (requestId, action, rejectionReason) => {
                          const res = await decideTimeOffRequest(requestId, action, rejectionReason)
                          if (!res.ok) {
                              showToast(res.error, 'error')
                              return false
                          }
                          // An approval deducts the ledger, so refresh balances too.
                          const timeOffRes = await fetchEmployeeTimeOff(employeeId)
                          if (timeOffRes.ok) {
                              setProfileLeaveRequests(timeOffRes.data.requests ?? [])
                              setProfileLeaveBalances(timeOffRes.data.balances ?? [])
                          }
                          return true
                      }
                    : undefined
            }
        />
    )

    return profileView
}
